/**
 * Modern Discount Engine for iWishBag
 * Handles all discount calculations with multi-currency support
 */

import { supabase } from '@/integrations/supabase/client';
import { currencyService } from './CurrencyService';

export interface DiscountRule {
  target: 'total' | 'shipping_cost' | 'customs_duty' | 'handling_fee' | 'local_delivery_fee' | 'local_tax_amount' | 'gateway_fee' | 'insurance_amount';
  discount_type: 'percentage' | 'fixed_amount' | 'free' | 'cashback';
  discount_value: number;
  max_discount_amount?: number;
  min_order_value?: number;
  min_weight?: number;
  weight_unit?: 'kg' | 'lbs';
  currency: string;
  countries?: string[];
  cities?: string[];
  payment_methods?: string[];
  description: string;
  auto_apply?: boolean;
}

export interface DiscountConfig {
  id: string;
  name: string;
  description?: string;
  type: 'coupon' | 'auto_weight' | 'auto_country' | 'auto_regional' | 'campaign' | 'payment_gateway';
  code?: string;
  rules: DiscountRule;
  is_active: boolean;
  usage_count: number;
  usage_limit?: number;
  valid_from?: string;
  valid_until?: string;
}

export interface AppliedDiscount {
  id: string;
  name: string;
  type: string;
  code?: string;
  target: string;
  original_amount: number;
  discount_amount: number;
  final_amount: number;
  currency: string;
  description: string;
  applied_at: string;
}

export interface QuoteComponents {
  total: number;
  items_subtotal: number;
  shipping_cost: number;
  customs_duty: number;
  handling_fee: number;
  local_delivery_fee?: number;
  local_tax_amount?: number;
  gateway_fee?: number;
  insurance_amount?: number;
}

export class DiscountEngine {
  private static instance: DiscountEngine;
  
  public static getInstance(): DiscountEngine {
    if (!DiscountEngine.instance) {
      DiscountEngine.instance = new DiscountEngine();
    }
    return DiscountEngine.instance;
  }

  /**
   * Get all auto-applicable discounts for a quote
   */
  async getAutoDiscounts(
    country: string,
    totalWeight: number,
    quoteComponents: QuoteComponents,
    customerCurrency: string,
    paymentMethod?: string,
    city?: string
  ): Promise<AppliedDiscount[]> {
    try {
      console.log('[DISCOUNT-ENGINE] Getting auto-discounts for:', { country, totalWeight, paymentMethod, city });

      const { data: autoDiscounts, error } = await supabase
        .from('discount_configs')
        .select('*')
        .eq('is_active', true)
        .in('type', ['auto_weight', 'auto_country', 'auto_regional', 'payment_gateway'])
        .or(`valid_until.is.null,valid_until.gte.${new Date().toISOString()}`);

      if (error) {
        console.error('[DISCOUNT-ENGINE] Error fetching auto-discounts:', error);
        return [];
      }

      const applicableDiscounts: AppliedDiscount[] = [];

      for (const discount of autoDiscounts || []) {
        
        
        const applied = await this.checkAndApplyDiscount(
          discount,
          { country, totalWeight, quoteComponents, customerCurrency, paymentMethod, city }
        );
        
        if (applied) {
          
          applicableDiscounts.push(applied);
        } else {
          
        }
      }

      console.log('[DISCOUNT-ENGINE] Applied auto-discounts:', applicableDiscounts.length);
      return applicableDiscounts;
      
    } catch (error) {
      console.error('[DISCOUNT-ENGINE] Error in getAutoDiscounts:', error);
      return [];
    }
  }

  /**
   * Validate and apply a coupon code
   */
  async validateCouponCode(
    code: string,
    country: string,
    quoteComponents: QuoteComponents,
    customerCurrency: string
  ): Promise<{ valid: boolean; discount?: AppliedDiscount; error?: string }> {
    try {
      console.log('[DISCOUNT-ENGINE] Validating coupon:', code);

      const { data: coupon, error } = await supabase
        .from('discount_configs')
        .select('*')
        .eq('code', code.toUpperCase())
        .eq('type', 'coupon')
        .eq('is_active', true)
        .or(`valid_until.is.null,valid_until.gte.${new Date().toISOString()}`)
        .single();

      if (error || !coupon) {
        return { valid: false, error: 'Invalid or expired coupon code' };
      }

      // Check usage limit
      if (coupon.usage_limit && coupon.usage_count >= coupon.usage_limit) {
        return { valid: false, error: 'Coupon usage limit exceeded' };
      }

      const applied = await this.checkAndApplyDiscount(
        coupon,
        { country, quoteComponents, customerCurrency }
      );

      if (!applied) {
        return { valid: false, error: 'Coupon not applicable to your order' };
      }

      // Increment usage count
      await supabase
        .from('discount_configs')
        .update({ usage_count: coupon.usage_count + 1 })
        .eq('id', coupon.id);

      return { valid: true, discount: applied };
      
    } catch (error) {
      console.error('[DISCOUNT-ENGINE] Error validating coupon:', error);
      return { valid: false, error: 'Failed to validate coupon' };
    }
  }

  /**
   * Check if discount is applicable and calculate discount amount
   */
  private async checkAndApplyDiscount(
    discount: DiscountConfig,
    context: {
      country: string;
      totalWeight?: number;
      quoteComponents: QuoteComponents;
      customerCurrency: string;
      paymentMethod?: string;
      city?: string;
    }
  ): Promise<AppliedDiscount | null> {
    const { country, totalWeight, quoteComponents, customerCurrency, paymentMethod, city } = context;
    const rules = discount.rules;

    // Check country eligibility
    if (rules.countries && !rules.countries.includes(country)) {
      return null;
    }

    // Check city eligibility
    
    
    // FIXED: Proper city filtering logic that handles undefined/null city values
    if (rules.cities && rules.cities.length > 0) {
      // If rule has city restrictions, city must be provided and must be in the list
      if (!city || city === 'undefined' || !rules.cities.includes(city)) {
        
        
        return null;
      }
      
    } else {
      
    }

    // Check payment method eligibility
    if (rules.payment_methods && paymentMethod && !rules.payment_methods.includes(paymentMethod)) {
      return null;
    }

    // Check minimum order value
    if (rules.min_order_value && quoteComponents.total < rules.min_order_value) {
      return null;
    }

    // Check minimum weight (for weight-based discounts)
    if (rules.min_weight && totalWeight && totalWeight < rules.min_weight) {
      return null;
    }

    // Get target amount - support both old 'target' and new 'component' format
    const target = (rules as any).component || rules.target;
    const targetAmount = this.getTargetAmount(target, quoteComponents);
    if (targetAmount <= 0) {
      return null;
    }

    // Calculate discount amount
    
    
    
    const discountAmount = await this.calculateDiscountAmount(
      targetAmount,
      rules,
      customerCurrency
    );
    
    
    if (discountAmount <= 0) {
      return null;
    }

    const finalAmount = Math.max(0, targetAmount - discountAmount);

    return {
      id: discount.id,
      name: discount.name,
      type: discount.type,
      code: discount.code,
      target: target,
      original_amount: targetAmount,
      discount_amount: discountAmount,
      final_amount: finalAmount,
      currency: customerCurrency,
      description: rules.description,
      applied_at: new Date().toISOString()
    };
  }

  /**
   * Calculate the actual discount amount based on rules
   */
  private async calculateDiscountAmount(
    targetAmount: number,
    rules: DiscountRule,
    customerCurrency: string
  ): Promise<number> {
    let discountAmount = 0;

    switch (rules.discount_type) {
      case 'percentage':
        discountAmount = (targetAmount * rules.discount_value) / 100;
        break;
        
      case 'fixed_amount':
        // Convert from rule currency to customer currency
        discountAmount = await this.convertCurrency(
          rules.discount_value,
          rules.currency,
          customerCurrency
        );
        discountAmount = Math.min(discountAmount, targetAmount);
        break;
        
      case 'free':
        discountAmount = targetAmount;
        break;
        
      case 'cashback':
        discountAmount = (targetAmount * rules.discount_value) / 100;
        break;
    }

    // Apply maximum discount limit if specified
    if (rules.max_discount_amount) {
      const maxDiscountInCustomerCurrency = await this.convertCurrency(
        rules.max_discount_amount,
        rules.currency,
        customerCurrency
      );
      discountAmount = Math.min(discountAmount, maxDiscountInCustomerCurrency);
    }

    return Math.round(discountAmount * 100) / 100; // Round to 2 decimal places
  }

  /**
   * Get the target amount from quote components
   */
  private getTargetAmount(target: string, components: QuoteComponents): number {
    switch (target) {
      case 'total':
        return components.total;
      case 'shipping':
      case 'shipping_cost':
        return components.shipping_cost;
      case 'customs':
      case 'customs_duty':
        return components.customs_duty;
      case 'handling':
      case 'handling_fee':
        return components.handling_fee;
      case 'delivery':
      case 'local_delivery_fee':
        return components.local_delivery_fee || 0;
      case 'taxes':
      case 'local_tax_amount':
        return components.local_tax_amount || 0;
      case 'gateway_fee':
        return components.gateway_fee || 0;
      case 'insurance_amount':
        return components.insurance_amount || 0;
      default:
        return 0;
    }
  }

  /**
   * Convert currency using existing CurrencyService
   */
  private async convertCurrency(amount: number, fromCurrency: string, toCurrency: string): Promise<number> {
    if (fromCurrency === toCurrency) {
      return amount;
    }

    try {
      return await currencyService.convertAmount(amount, fromCurrency, toCurrency);
    } catch (error) {
      console.warn('[DISCOUNT-ENGINE] Currency conversion failed, using original amount:', error);
      return amount;
    }
  }

  /**
   * Calculate total savings from multiple discounts
   */
  calculateTotalSavings(discounts: AppliedDiscount[]): number {
    return discounts.reduce((total, discount) => total + discount.discount_amount, 0);
  }

  /**
   * Smart stacking: Apply best combination of discounts
   */
  smartStackDiscounts(
    autoDiscounts: AppliedDiscount[],
    couponDiscount?: AppliedDiscount
  ): AppliedDiscount[] {
    // Always include auto-discounts (weight, country, payment method)
    const result = [...autoDiscounts];

    // For coupon discounts, check if they provide better value
    if (couponDiscount) {
      const existingTotalDiscount = autoDiscounts.find(d => d.target === 'total');
      
      if (existingTotalDiscount) {
        // If coupon provides better total discount, replace the existing one
        if (couponDiscount.discount_amount > existingTotalDiscount.discount_amount) {
          const index = result.findIndex(d => d.id === existingTotalDiscount.id);
          result[index] = couponDiscount;
        }
        // Otherwise, keep the existing auto-discount
      } else {
        // No competing total discount, add the coupon
        result.push(couponDiscount);
      }
    }

    return result;
  }
}

// Export singleton instance
export const discountEngine = DiscountEngine.getInstance();